import {
  ButtonDesigns,
  ButtonTypes,
  getFontCss,
  HoverableLink,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectPatchingOrganizationInviteFromState } from 'src/app/OrganizationInvites/redux/organizationInvites.selectors'
import { DeclineOrganizationInviteMessage } from 'src/app/Toast/Toast'
import AppName from 'src/components/AppName/AppName'
import BrandAgnosticFooter from 'src/components/BrandAgnosticFooter/BrandAgnosticFooter'
import BrandPrimaryLogo from 'src/components/BrandPrimaryLogo/BrandPrimaryLogo'
import Button from 'src/components/Button/Button'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import TestId from 'src/components/TestId/TestId'
import UnauthedBox from 'src/components/UnauthedBox/UnauthedBox'
import UnauthedBoxContainer from 'src/components/UnauthedBoxContainer/UnauthedBoxContainer'
import UnauthedPageContainer from 'src/components/UnauthedPageContainer/UnauthedPageContainer'
import { OrganizationRoles } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { OrganizationInviteModel } from 'src/models/organizations.models'
import { OrgInviteStatus } from 'src/models/people.models'
import { connectedReplace } from 'src/navigation/Lnk'
import { homeUrl, manageOrgInviteUrl, onboardingUrl } from 'src/navigation/urls'
import { putOrganizationInvite } from 'src/redux/actions/data/organizations.actions'
import {
  selectAcceptOrgInviteFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectOrganizationInvitesModelsFromState } from 'src/redux/selectors/data/organizations.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const OrgInviteDecision: React.FC = () => {
  const translate = useTranslate()

  const appName = useSelector(selectAppName)
  const orgInvites = useSelector(selectOrganizationInvitesModelsFromState)
  const acceptOrgInvite = useSelector(selectAcceptOrgInviteFromState)
  const user = useSelector(selectUserListModelFromState)
  const isPatchingOrganizationInvite = useSelector(
    selectPatchingOrganizationInviteFromState,
  )

  const { analytics } = useAnalyticsContext()

  const firstOrgInvite = Object.values(orgInvites)[0] as OrganizationInviteModel

  const dispatch = useDispatch()

  // force redirect if no org invites
  useEffect((): void => {
    if (
      Object.values(orgInvites).length === 0 ||
      acceptOrgInvite ||
      user?.isInExplicitOrg
    ) {
      dispatch(connectedReplace(homeUrl()))
    }
  }, [orgInvites, acceptOrgInvite, user])

  return (
    <>
      {Object.values(orgInvites).length === 0 ||
      acceptOrgInvite ||
      user?.isInExplicitOrg ||
      isPatchingOrganizationInvite ? (
        <LoadingBackgroundPage />
      ) : (
        <div
          css={css`
            width: 100%;
          `}
        >
          {/* TODO: rename title */}
          <Helmet>
            <title>{`Get started today and create virtual cards with the card in your pocket. | ${appName}`}</title>
            <meta
              name="description"
              content={`Create an ${appName} account to create, send, and manage virtual cards for all your business payments.`}
            />
          </Helmet>
          <UnauthedPageContainer
            topLeftImage={
              <div
                css={css`
                  position: relative;
                  top: 30px;
                  left: 30px;
                `}
              >
                <BrandPrimaryLogo />
              </div>
            }
          >
            <UnauthedBoxContainer
              css={css`
                display: flex;
                padding: 120px;
                max-width: 1200px;
              `}
              noBox
              hideBrandLogo
              footer={
                <div
                  css={css`
                    display: flex;
                    justify-content: center;
                    z-index: 1;
                  `}
                >
                  <BrandAgnosticFooter
                    css={css`
                      flex-grow: 1;
                      width: auto;
                    `}
                    logOutOption
                  />
                </div>
              }
            >
              <UnauthedBox
                css={css`
                  padding: 0px;
                  max-width: 600px;
                  width: 100%;
                  z-index: 1;
                `}
                noInnerPadding
              >
                <div
                  css={css`
                    padding: 80px;
                  `}
                >
                  <div
                    css={css`
                      ${getFontCss(
                        32,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.medium,
                      )};
                      margin-bottom: 40px;
                    `}
                  >
                    {translate('It looks like you have a pending invitation')}
                  </div>
                  <div
                    css={css`
                      ${getFontCss(
                        16,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.regular,
                      )};
                      margin-bottom: 20px;
                    `}
                  >
                    {translate('Thanks for signing up with ${appName}!', {
                      appName: <AppName />,
                    })}
                  </div>
                  <div
                    css={css`
                      ${getFontCss(
                        16,
                        themeConstants.colors.font_primary,
                        styleConstants.fontFamilies.regular,
                      )};
                      margin-bottom: 40px;
                    `}
                  >
                    {translate(
                      "${bolded}. You can only be in one org at a time, so please choose if you'd like to join or set up your own.",
                      {
                        bolded: (
                          <span
                            css={css`
                              ${getFontCss(
                                16,
                                themeConstants.colors.font_primary,
                                styleConstants.fontFamilies.medium,
                              )};
                            `}
                          >
                            {translate(
                              "It looks like someone has already invited you to join their company's org in ${appName}",
                              {
                                appName: <AppName />,
                              },
                            )}
                          </span>
                        ),
                      },
                    )}
                  </div>
                  <div
                    css={css`
                      border-radius: 10px;
                      border: 1px solid ${styleConstants.colors.gray_medium};
                      background-color: ${styleConstants.colors.gray_light};
                      padding: 20px;
                    `}
                  >
                    <div
                      css={css`
                        ${getFontCss(
                          20,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                        margin-bottom: 20px;
                      `}
                    >
                      {translate('Review invitation')}
                    </div>
                    <div
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.regular,
                        )};
                        margin-bottom: 20px;
                      `}
                    >
                      {translate(
                        firstOrgInvite?.role === OrganizationRoles.admin
                          ? '${senderNameAndEmail} has invited you to be an ${role} in their company in ${appName}.'
                          : '${senderNameAndEmail} has invited you to be a ${role} in their company in ${appName}.',
                        {
                          senderNameAndEmail: (
                            <span
                              css={css`
                                ${getFontCss(
                                  16,
                                  themeConstants.colors.font_primary,
                                  styleConstants.fontFamilies.medium,
                                )};
                              `}
                            >
                              {`${firstOrgInvite?.inviter.fullNameOrEmail} (`}
                              <span>
                                <HoverableLink
                                  size={HoverableLinkSizes.Large}
                                  href={`mailto:${firstOrgInvite?.inviter.email}`}
                                  underline
                                  ariaLabelSource={
                                    firstOrgInvite?.inviter.email
                                  }
                                >
                                  {firstOrgInvite?.inviter.email}
                                </HoverableLink>
                              </span>
                              {`)`}
                            </span>
                          ),
                          role: (
                            <span>
                              {translate(firstOrgInvite?.orgRoleDisplayTitle)}
                            </span>
                          ),
                          appName: <AppName />,
                        },
                      )}
                    </div>
                    <Button
                      css={css`
                        width: auto;
                        display: inline-block;
                      `}
                      type={ButtonTypes.button}
                      design={ButtonDesigns.designCta}
                      big
                      text={
                        <TestId testId="reviewInvitation">
                          {translate('Review Invitation')}
                        </TestId>
                      }
                      linkProps={{
                        to: manageOrgInviteUrl(firstOrgInvite?.id),
                        ariaLabelSource: 'Review Invitation',
                      }}
                      onClick={(): void => {
                        analytics?.track(
                          'accept invite or onboard - review invitation',
                          {
                            intent: 'review invitation',
                            interaction: 'button press',
                          },
                        )
                      }}
                    />
                  </div>
                  <div
                    css={css`
                      margin: 20px 0px;
                      display: flex;
                      justify-content: center;
                      align-items: center;
                    `}
                  >
                    <DividerLineDiv />
                    <div
                      css={css`
                        ${getFontCss(
                          16,
                          styleConstants.colors.gray_darkest,
                          styleConstants.fontFamilies.mediumitalic,
                        )};
                      `}
                    >
                      {translate('or')}
                    </div>
                    <DividerLineDiv />
                  </div>
                  <div
                    css={css`
                      border-radius: 10px;
                      border: 1px solid ${styleConstants.colors.gray_medium};
                      background-color: ${styleConstants.colors.gray_light};
                      padding: 20px;
                    `}
                  >
                    <div
                      css={css`
                        ${getFontCss(
                          20,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                        margin-bottom: 20px;
                      `}
                    >
                      {translate('Continue setting up your company')}
                    </div>
                    <div
                      css={css`
                        ${getFontCss(
                          16,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.regular,
                        )};
                        margin-bottom: 20px;
                      `}
                    >
                      {translate(
                        'Decline the invitation and instead continue setting up a new org in ${appName} for your company.',
                        {
                          appName: <AppName />,
                        },
                      )}
                    </div>
                    <Button
                      css={css`
                        width: auto;
                        display: inline-block;
                      `}
                      type={ButtonTypes.button}
                      design={ButtonDesigns.designCtaInverse}
                      big
                      text={translate('Decline and Continue Setup')}
                      linkProps={{
                        to: onboardingUrl(),
                        ariaLabelSource: 'Decline and Continue Setup',
                      }}
                      onClick={(): void => {
                        analytics?.track(
                          'accept invite or onboard - decline and continue set up',
                          {
                            intent: 'decline and continue set up',
                            interaction: 'button press',
                          },
                        )

                        dispatch(
                          putOrganizationInvite(
                            OrgInviteStatus.Rejected,
                            firstOrgInvite,
                            <DeclineOrganizationInviteMessage
                              organizationInvite={firstOrgInvite}
                            />,
                          ),
                        )
                      }}
                    />
                  </div>
                </div>
              </UnauthedBox>
            </UnauthedBoxContainer>
          </UnauthedPageContainer>
        </div>
      )}
    </>
  )
}

export default OrgInviteDecision
